import React, { useState, useEffect } from 'react';
import './App.css';
import { motion, AnimatePresence } from "framer-motion";
import SnakeGame from './components/SnakeGame';
import TypingTest from './components/TypingTest';
import DevConsole from './components/DevConsole';
import { InteractiveGridPattern } from './components/ui/interactive-grid-pattern';

function App() {
  // =====================================================
  // STATE MANAGEMENT
  // =====================================================
  
  // AI Chatbot state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: 'Hi! I\'m Jade\'s AI assistant. Ask me anything about my experience, projects, or skills!' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  
  // Games and interactive features state
  const [snakeGameOpen, setSnakeGameOpen] = useState(false);
  const [typingTestOpen, setTypingTestOpen] = useState(false);
  const [devConsoleOpen, setDevConsoleOpen] = useState(false);
  const [matrixMode, setMatrixMode] = useState(false);
  
  // Dark mode state
  const [darkMode] = useState(false);
  
  // Projects carousel state
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  
  // Projects modal state
  const [projectsModalOpen, setProjectsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [slideshowOpen, setSlideshowOpen] = useState(false);
  const [currentCarouselImageIndex, setCurrentCarouselImageIndex] = useState(0);
  
  // Email copied feedback state
  const [emailCopied, setEmailCopied] = useState(false);

  // =====================================================
  // INTERACTIVE FEATURES (Konami Code, Hotkeys)
  // =====================================================
  
  useEffect(() => {
    const KONAMI = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
    let konamiSequence = [];
    
    const handleKeyPress = (e) => {
      // Snake game hotkey (G)
      if (e.key.toLowerCase() === 'g' && !chatOpen && !snakeGameOpen && !typingTestOpen && !devConsoleOpen) {
        setSnakeGameOpen(true);
        return;
      }
      
      // Typing test hotkey (T)
      if (e.key.toLowerCase() === 't' && !chatOpen && !snakeGameOpen && !typingTestOpen && !devConsoleOpen) {
        setTypingTestOpen(true);
        return;
      }
      
      // Dev console hotkey (~)
      if (e.key === '`' && !chatOpen && !snakeGameOpen && !typingTestOpen && !devConsoleOpen) {
        e.preventDefault();
        setDevConsoleOpen(true);
        return;
      }
      
      // Konami code detection
      konamiSequence = [...konamiSequence, e.key].slice(-10);
      if (konamiSequence.join(',') === KONAMI.join(',')) {
        setMatrixMode(m => !m);
        setTimeout(() => {
          alert('Matrix Mode ' + (!matrixMode ? 'ACTIVATED' : 'DEACTIVATED') + '!');
        }, 100);
        konamiSequence = [];
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [chatOpen, snakeGameOpen, typingTestOpen, devConsoleOpen, matrixMode]);

  // =====================================================
  // EVENT HANDLERS
  // =====================================================
  
  const handleEmailClick = async () => {
    try {
      await navigator.clipboard.writeText('jadedelara25@gmail.com');
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
      // Fallback: open email client
      window.location.href = 'mailto:jadedelara25@gmail.com';
    }
  };

  // =====================================================
  // DATA DEFINITIONS
  // =====================================================
  
  // Tech Stack Data
  const techStack = {
    'Frontend': ['JavaScript', 'TypeScript', 'React', 'Next.js', 'Vue.js', 'Tailwind CSS'],
    'Backend': ['Node.js', 'Python', 'PHP', 'Laravel', 'WebSocket', 'MongoDB'],
    'DevOps & Cloud': ['AWS', 'Hostinger', 'Google Cloud', 'GitHub Actions']
  };

  // Experience Data
  const experience = [
    {
      role: 'SAP BTP Intern',
      company: 'Accenture',
      location: 'Hybrid',
      period: 'Jan 2026 - May 2026',
      type: 'Internship',
      description: 'Participated in an intensive SAP and SAP BTP bootcamp focused on enterprise solutions, integration, and business process technologies. Completed hands-on activities, case studies, and technical exercises involving SAP PI/PO Integration Builder, ESR, and SAP BTP concepts while gaining foundational knowledge in SAP implementation and integration workflows. Earned the SAP Implementation Consultant – End-to-End Business Processes for SAP Business Suite certification.'
    },
    {
      role: 'Lead Developer',
      company: 'Quizzle - Capstone Project',
      location: 'University of Santo Tomas',
      period: '2025-2026',
      type: 'Academic Project',
      description: 'Developed a full-stack web application using React, Node.js, Firebase, and AI APIs to deliver personalized learning content and adaptive quizzes. Built secure RESTful APIs for authentication, quiz generation, and progress tracking while collaborating closely with a small development team using Git and code reviews to ensure quality and scalability.'
    },
    {
      role: 'System Analyst',
      company: 'Software Engineering - Drafter Autotech',
      location: 'Extension, B113 L12 Mindanao Avenue, corner Regalado Hwy, Greater Lagro',
      period: '2025',
      type: 'Academic Project',
      description: 'Led team of 7 in developing inventory management system that improved operational efficiency by 20% and reduced errors by 35%.'
    },
    {
      role: 'Junior UI/UX Designer & SMM',
      company: "The Manning's Realty Group",
      location: 'Remote',
      period: 'Aug 2022 - Dec 2024',
      type: 'Full-time',
      description: 'Handled both design and digital marketing initiatives by creating UI assets and managing social media platforms. Built internal automation tools and an agent onboarding website that reduced onboarding time and improved workflow efficiency. Managed property listings across multiple platforms while driving significant growth in social media engagement through data-driven content strategies.'
    }
  ];

  // Projects Data
  const projects = [
    {
      title: 'CerthPath',
      category: 'fullstack',
      description: 'CertPath is a web app that generates a personalized IT certification roadmap using AI. Users answer 5 quick questions about their career track, experience level, and goals, then receive a step-by-step path with exam details, costs, passing scores, and direct certification links.',
      technologies: ['React.js', 'GroqAI', 'Llama 3.3 70B'],
      link: 'https://certpath-six.vercel.app/',
      rating: '25+ Providers',
      date: '2026',
      image: '/Certpathlogo.PNG',
      imageFit: 'contain',
      carouselImages: [
        '/certpath1.PNG',
        '/certpath2.PNG',
        '/certpath3.PNG',
        '/certpath4.PNG',
        '/certpath5.PNG',
        '/certpath6.PNG',
        '/certpath7.PNG',
        '/certpath8.PNG'
      ]
    },
    {
      title: 'Gramatika - Filipino Grammar Checker',
      category: 'frontend',
      description: 'Google Chrome extension inspired by Grammarly, providing grammar checking and writing assistance for Filipino/Tagalog language.',
      technologies: ['JavaScript', 'Chrome Extension API', 'HTML', 'CSS'],
      link: '#',
      rating: 'Extension',
      date: '2026',
      image: '/gramatika.JPG'
    },
    {
      title: 'Quizzle - AI-Powered Adaptive Learning Platform',
      category: 'fullstack',
      description: 'Academic capstone project: Full-stack AI-powered learning platform featuring adaptive quizzes and AI-powered notes that reorganize and restructure content. Our AI functions work seamlessly across games, quizzes, and more, delivering 96.15% user satisfaction.',
      technologies: ['React.js', 'Node.js', 'Firebase', 'Llama AI API'],
      link: 'https://www.quizzlelearn.com',
      rating: '96.15%',
      date: '2025-2026',
      image: '/6.jpg',
      carouselImages: ['/6.jpg', '/7.jpg', '/8.jpg', '/9.jpg', '/10.jpg', '/11.jpg', '/12.jpg']
    },
    {
      title: "Drafter Autotech's Inventory System",
      category: 'fullstack',
      description: 'Led as System Analyst for this academic project: Successfully integrated a comprehensive inventory management system that transformed operations. The before and after images showcase the dramatic improvement in efficiency (20% increase) and accuracy (35% error reduction) with real-time validation.',
      technologies: ['Laravel', 'MySQL', 'PHP', 'RESTful APIs'],
      link: '#',
      rating: '4.5/5',
      date: '2025',
      image: '/1.jpg',
      carouselImages: ['/1.jpg', '/2.jpg', '/3.jpg', '/4.jpg', '/5.jpg']
    },
    {
      title: 'The Mannings Realty Agent Portal',
      category: 'frontend',
      description: 'Agent onboarding website built with ClickFunnels that reduced onboarding time by 40% for new real estate agents.',
      technologies: ['ClickFunnels', 'JavaScript', 'CSS3'],
      link: '#',
      rating: 'Commercial',
      date: '2022-2024',
      image: '/13.jpg',
      carouselImages: ['/13.jpg', '/14.jpg', '/15.jpg', '/16.jpg', '/17.jpg']
    }
  ];

  // Certifications Data
  const certifications = [
    { title: 'Gemini Certified University Student', link: 'https://edu.google.accredible.com/881a73bc-2d6a-467d-941a-f2d30b83eba3', issuer: 'Google for Education', date: 'June 2026', image: '/geminicert.png' },
    { title: 'SAP Certified - Implementation Consultant - End-to-End Business Processes for SAP Business Suite', link: 'https://www.credly.com/badges/d71bc85e-4cfc-458c-9ed3-b9b43db12215', issuer: 'SAP', date: 'February 2026', image: '/18.png' },
    { title: 'Managing Clean Core for SAP S/4HANA Cloud', link: 'https://badger.learning.sap.com/verify/xykir-sifom-retyb-bygig-kyvon', issuer: 'SAP', date: 'Feb 2026', image: '/sap.jpg' },
    { title: 'Information Technology Passport (IP) Certification', link: 'https://philnits.org/passers-ip/', issuer: 'PhilNITS', date: 'Nov 2025', credentialId: 'IP4500103', image: '/philnitrs.jpg' },
    { title: 'Generative AI Fundamentals', link: 'https://credentials.databricks.com/51164e68-f0dc-4d32-ad50-4d97f059a8f2#acc.tZRH5UXj', issuer: 'Databricks', date: 'Sep 2025', image: '/databricks.jpg' },
    { title: 'Introduction to Generative AI', link: 'https://www.skills.google/public_profiles/19ab71dd-9659-4afc-bf0e-11af3f0c68ab/badges/18392661', issuer: 'Google Cloud Skills Boost', date: 'Sep 2025', image: '/google.jpg' },
    { title: 'CCNA: Introduction to Networks', link: 'https://www.credly.com/badges/4e4be44b-a2e1-4c08-886e-003d20e0ddcf/public_url', issuer: 'Cisco Networking Academy', date: 'Jan 2024', credentialId: '4e4be44b-a2e1-4c08-886e-003d20e0ddcf', image: '/cisco.jpg' },
    { title: 'CCNA: Switching, Routing, and Wireless Essentials', link: 'https://www.credly.com/badges/52b152d9-cf74-4bd5-bbca-06e98bfaf10f/public_url', issuer: 'Cisco Networking Academy', date: 'June 2024', credentialId: '52b152d9-cf74-4bd5-bbca-06e98bfaf10f', image: '/cisco.jpg' }
  ];

  // =====================================================
  // INITIALIZATION & EFFECTS
  // =====================================================

  // Optimized particles configuration (currently not in use)
  // const particlesConfig = {
  //   particles: {
  //     number: { value: 30, density: { enable: true, area: 1000 } }, // Reduced from 60 to 30
  //     color: { value: "#10b981" },
  //     shape: { type: "circle" },
  //     opacity: { value: 0.4, random: false }, // Fixed opacity for better performance
  //     size: { value: 3, random: true },
  //     links: {
  //       enable: true,
  //       distance: 120, // Reduced from 150
  //       color: "#10b981",
  //       opacity: 0.3,
  //       width: 1
  //     },
  //     move: {
  //       enable: true,
  //       speed: 0.8, // Slower, smoother
  //       direction: "none",
  //       random: false,
  //       straight: false,
  //       outModes: { default: "bounce" }
  //     }
  //   },
  //   interactivity: {
  //     events: {
  //       onHover: { enable: true, mode: "grab" },
  //       onClick: { enable: true, mode: "push" },
  //       resize: true
  //     },
  //     modes: {
  //       grab: { distance: 100, links: { opacity: 0.5 } }, // Reduced distance
  //       push: { quantity: 2 } // Reduced from 4 to 2
  //     }
  //   },
  //   detectRetina: true,
  //   fpsLimit: 60 // Cap at 60fps
  // };

  // =====================================================
  // AI CHATBOT FUNCTIONS
  // =====================================================
  // AI CHATBOT FUNCTIONS
  // =====================================================
  
  const GROQ_API_KEY = process.env.REACT_APP_GROQ_API_KEY;
  const GROQ_API_URL = process.env.REACT_APP_GROQ_API_URL || 'https://api.groq.com/openai/v1/chat/completions';
  
  const generateAIResponse = async (userMessage) => {
    if (chatLoading) return; // Prevent multiple simultaneous requests
    
    if (!GROQ_API_KEY) {
      setChatMessages(prev => [
        ...prev,
        { role: 'user', content: userMessage },
        { role: 'assistant', content: '⚠️ API key not configured. Please add REACT_APP_GROQ_API_KEY to your .env file.' }
      ]);
      return;
    }
    
    setChatLoading(true);
    
    const context = `You are an AI assistant representing Jade Lloyd N. de Lara, a professional full-stack web developer and system analyst. 
    
    Key information about Jade:
    - Full name: Jade Lloyd N. de Lara
    - Born: September 26, 2003 (age 22)
    - Education: BS Computer Science student at University of Santo Tomas (UST), currently in senior year (2026)
    - Full-stack web developer with expertise in JavaScript, TypeScript, React, Next.js, Vue.js, Node.js, Python, PHP, Laravel
    - Currently: Accenture SAP ABAP Academy Delegate (2026 Internship) - Learning enterprise SAP ABAP development
    - Experience:
      * Lead Developer - Quizzle Capstone Project (2025-2026): Built AI-powered adaptive learning platform with 96.15% user satisfaction, integrated Llama AI API with 4.51/5.0 accuracy rating
      * System Analyst - Drafter Autotech Inventory System (2025): Led team of 7, improved operational efficiency by 20% and reduced errors by 35%
      * Junior UI/UX Designer & SMM - The Mannings Realty Group (Aug 2022 - Dec 2024): Increased Facebook engagement by 353% and Instagram reach by 596%, reduced agent onboarding time by 40% with custom portal
    - Notable projects: 
      * Gramatika (2026): Chrome extension for Filipino/Tagalog grammar checking
      * Quizzle (2025-2026): AI-powered adaptive learning platform, live at quizzlelearn.com
      * Drafter Autotech Inventory System (2025): Comprehensive inventory management system
      * The Mannings Realty Agent Portal (2022-2024): ClickFunnels-based onboarding system
    - Tech Stack: React, Next.js, Tailwind CSS, Node.js, Laravel, MySQL, PostgreSQL, Firebase, AWS, Docker, Kubernetes
    - Located in Metro Manila, Philippines
    - Contact: jadedelara25@gmail.com | Phone: 09668559513
    - Pet: Cola - black cat, mix of Siamese/Persian and Snowshoe
    - Hobbies: MMA, boxing, basketball, music, video games
    - Music taste: Hip hop, OPM, R&B, alternative rock, 90's whimsigoth, indie songs
    
    IMPORTANT LANGUAGE INSTRUCTIONS:
    - If the user speaks in Tagalog or uses Taglish (mix of Tagalog/English), respond in MODERN, CASUAL Taglish. Use Gen Z/millennial Filipino speak - NOT formal or poetic Tagalog.
    - If the user speaks in pure English, respond in professional English only.
    - Match the user's language style and tone - be natural and conversational.
    - Examples:
      * User: "pre ilang taon kana sa ust" → You: "Salamat pre! Currently nasa senior year na ako sa UST, BS Computer Science. Mga 4 years na rin, so graduating na this 2026!"
      * User: "may pet ka ba" → You: "Yes pre! May cat ako, si Cola. She's black, mix ng Siamese/Persian tsaka Snowshoe. Super cute niya haha!"
      * User: "what projects have you worked on" → You: "I've worked on several projects including Quizzle (AI learning platform with 96.15% satisfaction), Gramatika (Filipino grammar checker), and Drafter Autotech's inventory system that improved efficiency by 20%."
      * User: "anong music pinapakinggan mo" → You: "Sobrang diverse ng music taste ko bro! Hip hop, OPM, R&B, alternative rock, 90's whimsigoth, indie songs - basta solid yung vibe, bet ko yan!"
    
    Respond in a friendly manner as if you are Jade's personal AI assistant. Keep responses concise (2-3 sentences).
    Be helpful about projects, skills, and experience. If asked about contact info, provide the email and phone number above.`;
    
    try {
      const response = await fetch(GROQ_API_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${GROQ_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'llama-3.3-70b-versatile',
          messages: [
            { role: 'system', content: context },
            ...chatMessages.map(msg => ({ role: msg.role, content: msg.content })),
            { role: 'user', content: userMessage }
          ],
          temperature: 0.7,
          max_tokens: 200
        })
      });
      
      const data = await response.json();
      const aiMessage = data.choices[0]?.message?.content || 'Sorry, I couldn\'t generate a response.';
      
      setChatMessages(prev => [
        ...prev,
        { role: 'user', content: userMessage },
        { role: 'assistant', content: aiMessage }
      ]);
    } catch (error) {
      setChatMessages(prev => [
        ...prev,
        { role: 'user', content: userMessage },
        { role: 'assistant', content: 'Sorry, I encountered an error. Please try again!' }
      ]);
    }
    
    setChatLoading(false);
    setChatInput('');
  };
  
  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (chatInput.trim()) {
      generateAIResponse(chatInput.trim());
    }
  };

  // =====================================================
  // EVENT HANDLERS
  // =====================================================

  // Smooth scroll for anchor links
  useEffect(() => {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }, []);

  const currentYear = new Date().getFullYear();

  return (
    <div className={`App relative portfolio-shell ${darkMode ? 'dark-mode dark' : ''}`}>
      
      {/* =====================================================
          INTERACTIVE PARTICLE BACKGROUND
          ===================================================== */}
      
      {/* Temporarily disabled to show grid pattern */}
      {/* {particlesInit && <ParticleBackground config={particlesConfig} />} */}
      
      {/* =====================================================
          INTERACTIVE GRID PATTERN BACKGROUND
          ===================================================== */}
      
      <InteractiveGridPattern 
        className="fixed inset-0 z-0" 
        cellSize={64}
        proximity={140}
        glowColor="rgba(34, 197, 94, 0.12)"
        borderColor="rgba(34, 197, 94, 0.05)"
      />

      {/* =====================================================
          AI CHATBOT FLOATING BUTTON & MODAL
          ===================================================== */}
      
      <AnimatePresence>
        {chatOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 100 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 100 }}
            transition={{ type: "spring", damping: 20 }}
            className="fixed bottom-2 left-2 right-2 w-auto h-[85vh] sm:bottom-6 sm:right-6 sm:left-auto sm:w-96 sm:h-[500px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl z-50 overflow-hidden border border-jade-200 dark:border-gray-700 portfolio-surface"
          >
            {/* Chat Header */}
            <div className="bg-jade-600 p-3 sm:p-4 text-white flex justify-between items-center dark:bg-jade-700">
              <div>
                <h3 className="font-bold text-base sm:text-lg">AI Assistant</h3>
                <p className="text-xs opacity-90">Ask me anything!</p>
              </div>
              <button 
                onClick={() => setChatOpen(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Chat Messages */}
            <div className="h-[calc(85vh-140px)] sm:h-[360px] overflow-y-auto p-3 sm:p-4 space-y-3">
              {chatMessages.map((msg, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] p-3 rounded-lg ${
                    msg.role === 'user' 
                      ? 'bg-jade-500 text-white rounded-br-none' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-bl-none'
                  }`}>
                    {msg.content}
                  </div>
                </motion.div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 p-3 rounded-lg rounded-bl-none">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-jade-500 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-jade-500 rounded-full animate-bounce animation-delay-200"></div>
                      <div className="w-2 h-2 bg-jade-500 rounded-full animate-bounce animation-delay-400"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Chat Input */}
            <form onSubmit={handleChatSubmit} className="p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:border-jade-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  disabled={chatLoading}
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="submit"
                  disabled={chatLoading || !chatInput.trim()}
                  className="bg-jade-500 text-white p-2 rounded-full hover:bg-jade-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </motion.button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Chatbot Trigger Button */}
      <motion.button
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.85 }}
        onClick={() => setChatOpen(!chatOpen)}
        className="fixed bottom-6 right-6 bg-jade-600 hover:bg-jade-700 text-white p-4 rounded-full shadow-lg hover:shadow-2xl z-50 transition-all duration-150 border-2 border-white/80 font-bold"
      >
        <AnimatePresence mode="wait">
          {chatOpen ? (
            <motion.svg
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </motion.svg>
          ) : (
            <motion.svg
              key="chat"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </motion.svg>
          )}
        </AnimatePresence>
      </motion.button>

      {/* =====================================================
          ALL PROJECTS MODAL
          ===================================================== */}
      
      <AnimatePresence>
        {projectsModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
            onClick={() => setProjectsModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden portfolio-surface portfolio-surface-strong"
            >
              {/* Modal Header */}
              <div className="bg-jade-600 p-6 text-white flex justify-between items-center dark:bg-jade-700">
                <div>
                  <h2 className="text-2xl font-bold">All Projects</h2>
                  <p className="text-sm opacity-90 mt-1">Click on any project to view details</p>
                </div>
                <button 
                  onClick={() => setProjectsModalOpen(false)}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Projects Grid */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {projects.map((project, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="portfolio-surface group bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
                      onClick={() => {
                        setSelectedProject(project);
                        setCurrentCarouselImageIndex(0);
                        setSlideshowOpen(true);
                      }}
                    >
                      {/* Project Image */}
                      <div className="aspect-video bg-white dark:bg-gray-700 overflow-hidden relative border border-jade-100/60 dark:border-gray-600">
                        <img 
                          src={project.image} 
                          alt={project.title}
                          className={`w-full h-full transition-transform duration-500 ${project.imageFit === 'contain' ? 'object-contain p-4 group-hover:scale-105' : 'object-cover group-hover:scale-110'}`}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML += `
                              <div class="w-full h-full flex items-center justify-center text-jade-600 dark:text-jade-400">
                                <svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                </svg>
                              </div>
                            `;
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                          <span className="text-white font-semibold text-sm">Click to view slideshow</span>
                        </div>
                      </div>

                      {/* Project Info */}
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 group-hover:text-jade-600 dark:group-hover:text-jade-400 transition-colors line-clamp-2">
                            {project.title}
                          </h3>
                          <span className="px-2 py-1 bg-gradient-to-r from-jade-50 to-jade-100 dark:from-jade-900/40 dark:to-jade-800/40 text-jade-700 dark:text-jade-300 rounded-full text-xs font-semibold whitespace-nowrap ml-2">
                            {project.rating}
                          </span>
                        </div>
                        
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-2">
                          {project.description}
                        </p>
                        
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {project.technologies.slice(0, 3).map((tech) => (
                            <span
                              key={tech}
                              className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs"
                            >
                              {tech}
                            </span>
                          ))}
                          {project.technologies.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                              +{project.technologies.length - 3}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500 dark:text-gray-400">{project.date}</span>
                          {project.link !== '#' && (
                            <span className="text-jade-600 dark:text-jade-400 font-medium flex items-center gap-1">
                              View Live
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                              </svg>
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* =====================================================
          PROJECT SLIDESHOW MODAL
          ===================================================== */}
      
      <AnimatePresence>
        {slideshowOpen && selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
            onClick={() => {
              setSlideshowOpen(false);
              setSelectedProject(null);
              setCurrentCarouselImageIndex(0);
            }}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto portfolio-surface portfolio-surface-strong"
            >
              {/* Slideshow Header */}
              <div className="bg-jade-600 p-4 text-white flex justify-between items-center dark:bg-jade-700">
                <h3 className="text-xl font-bold">{selectedProject.title}</h3>
                <button 
                  onClick={() => {
                    setSlideshowOpen(false);
                    setSelectedProject(null);
                    setCurrentCarouselImageIndex(0);
                  }}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Slideshow Content */}
              <div className="p-4 sm:p-6 md:p-8">
                {/* Carousel Images */}
                {selectedProject.carouselImages && selectedProject.carouselImages.length > 0 ? (
                  <div className="space-y-4">
                    {/* Main Carousel Display */}
                    <div className="relative">
                      <div className="w-full max-h-[50vh] sm:max-h-[60vh] bg-white dark:bg-gray-700 rounded-lg overflow-hidden mb-4 flex items-center justify-center border border-jade-100/60 dark:border-gray-600">
                        <img 
                          src={selectedProject.carouselImages[currentCarouselImageIndex]} 
                          alt={`${selectedProject.title} - Screenshot ${currentCarouselImageIndex + 1}`}
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => {
                            e.target.parentElement.innerHTML = `
                              <div class="w-full h-full flex items-center justify-center text-jade-600 dark:text-jade-400">
                                <div class="text-center">
                                  <svg class="w-24 h-24 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                  </svg>
                                  <p class="text-lg font-semibold">Image not found</p>
                                </div>
                              </div>
                            `;
                          }}
                        />
                      </div>
                      
                      {/* Navigation Arrows */}
                      {selectedProject.carouselImages.length > 1 && (
                        <>
                          <button
                            onClick={() => setCurrentCarouselImageIndex((prev) => 
                              prev === 0 ? selectedProject.carouselImages.length - 1 : prev - 1
                            )}
                            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 bg-white/90 dark:bg-gray-800/90 text-jade-600 dark:text-jade-400 rounded-full hover:bg-white dark:hover:bg-gray-800 transition-all duration-300 shadow-lg"
                          >
                            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
                            </svg>
                          </button>
                          <button
                            onClick={() => setCurrentCarouselImageIndex((prev) => 
                              prev === selectedProject.carouselImages.length - 1 ? 0 : prev + 1
                            )}
                            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 bg-white/90 dark:bg-gray-800/90 text-jade-600 dark:text-jade-400 rounded-full hover:bg-white dark:hover:bg-gray-800 transition-all duration-300 shadow-lg"
                          >
                            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                    
                    {/* Image Counter */}
                    {
                      <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                        {currentCarouselImageIndex + 1} / {selectedProject.carouselImages.length}
                      </div>
                    }
                    
                    {/* Thumbnail Navigation */}
                    {
                      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-jade-400 scrollbar-track-gray-200 dark:scrollbar-track-gray-700">
                        {selectedProject.carouselImages.map((img, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentCarouselImageIndex(idx)}
                            className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                              idx === currentCarouselImageIndex 
                                ? 'border-jade-500 ring-2 ring-jade-300' 
                                : 'border-gray-300 dark:border-gray-600 hover:border-jade-400'
                            }`}
                          >
                            <img 
                              src={img} 
                              alt={`Thumbnail ${idx + 1}`} 
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    }
                  </div>
                ) : (
                  <div className="aspect-video bg-white dark:bg-gray-700 rounded-lg overflow-hidden mb-4 flex items-center justify-center border border-jade-100/60 dark:border-gray-600">
                    <div className="text-center text-gray-600 dark:text-gray-400">
                      <svg className="w-24 h-24 mx-auto mb-4 text-jade-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                      <p className="text-lg font-semibold mb-2">No Carousel Images</p>
                      <p className="text-sm">No images available for this project</p>
                    </div>
                  </div>
                )}
                
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Description</h4>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">{selectedProject.description}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Technologies</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.technologies.map((tech) => (
                        <span
                          key={tech}
                          className="px-3 py-1 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {selectedProject.link !== '#' && (
                    <div className="pt-4">
                      <a 
                        href={selectedProject.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-jade-600 text-white rounded-lg font-medium hover:bg-jade-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                      >
                        Visit Project Website
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                        </svg>
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content - Add relative z-10 to sit above particles */}
      <div className="relative z-10">
      
      {/* =====================================================
          HERO + ABOUT SECTION (COMBINED)
          ===================================================== */}
      
      <section className="portfolio-section bg-transparent py-8 px-4 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 bg-jade-200 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob-slow"></div>
          <div className="absolute top-60 right-10 w-96 h-96 bg-jade-300 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob-slow animation-delay-2000"></div>
          <div className="absolute bottom-20 left-1/3 w-96 h-96 bg-jade-100 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob-slow animation-delay-4000"></div>
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          {/* Header Card with Profile */}
          <div className="portfolio-surface portfolio-surface-strong bg-white/85 dark:bg-gray-800/75 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-6 border border-jade-100/50 dark:border-jade-500/20 animate-fadeInScale">
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Profile Photo - Replace src with your image path */}
              <div className="flex-shrink-0">
                <div className="relative group">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-jade-400 to-jade-600 rounded-full blur opacity-60 group-hover:opacity-100 transition duration-500"></div>
                  <div className="relative w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-jade-400 to-jade-600 shadow-lg">
                    {/* Replace the src below with your actual profile picture path */}
                    <img 
                      src="/jadenew.jpg" 
                      alt="Jade de Lara" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full flex items-center justify-center text-white text-3xl font-bold" style={{display: 'none'}}>
                      JD
                    </div>
                  </div>
                </div>
              </div>

              {/* Profile Info */}
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Jade de Lara</h1>
                  <svg className="w-5 h-5 text-jade-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-base text-gray-600 dark:text-gray-300">Software Engineer</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Integrating AI to build the future of web solutions</p>
                
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  <button 
                    onClick={handleEmailClick}
                    className={`portfolio-button group px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 border ${
                      emailCopied 
                        ? 'bg-green-50 dark:bg-green-900 text-green-700 dark:text-green-200 border-green-200 dark:border-green-600' 
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {emailCopied ? 'Copied!' : 'Email'}
                      <svg className="w-4 h-4 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {emailCopied ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                        )}
                      </svg>
                    </span>
                  </button>
                  <button 
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = '/Jade_de_Lara_RESUME.pdf';
                      link.download = 'Jade_de_Lara_RESUME.pdf';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="portfolio-button group px-4 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-300 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 border border-gray-200 dark:border-gray-600"
                  >
                    <span className="flex items-center gap-2">
                      CV
                      <svg className="w-4 h-4 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                      </svg>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Combined About and Tech Stack Card */}
          <div className="portfolio-surface bg-white/85 dark:bg-gray-800/75 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-6 transform hover:shadow-2xl transition-all duration-500 border border-gray-100 dark:border-gray-700 hover:border-jade-200 dark:hover:border-jade-500 animate-fadeInUp">
            <div className="grid md:grid-cols-2 gap-6">
              {/* About */}
              <div className="animate-slideInLeft">
                <div className="flex items-center gap-2 mb-3 group">
                  <div className="p-2 bg-jade-50 rounded-lg group-hover:bg-jade-100 transition-colors duration-300">
                    <svg className="w-5 h-5 text-jade-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">About</h2>
                </div>
                <div className="text-gray-700 text-sm space-y-2">
                  <p className="leading-relaxed animate-fadeIn">
                    Full-stack software engineer specializing in JavaScript, React, Python and, Laravel. Building modern web applications with focus on AI integration and user experience.
                  </p>
                  <p className="leading-relaxed animate-fadeIn animation-delay-200">
                    Developed academic projects and real-world applications leveraging AI tools and generative AI to deliver cutting-edge technology solutions.
                  </p>
                </div>
              </div>

              {/* Tech Stack */}
              <div className="animate-slideInRight">
                <div className="flex items-center gap-2 mb-3 group">
                  <div className="p-2 bg-jade-50 dark:bg-jade-900/30 rounded-lg group-hover:bg-jade-100 dark:group-hover:bg-jade-800/40 transition-colors duration-300">
                    <svg className="w-5 h-5 text-jade-600 dark:text-jade-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/>
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Tech Stack</h2>
                </div>
                
                {Object.entries(techStack).map(([category, technologies], idx) => (
                  <div key={category} className="mb-3 last:mb-0 animate-fadeIn" style={{animationDelay: `${idx * 100}ms`}}>
                    <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">{category}</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {technologies.map((tech, techIdx) => (
                        <span
                          key={tech}
                          className="portfolio-chip px-2.5 py-1 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 text-gray-800 dark:text-gray-200 rounded-md text-xs font-medium hover:from-jade-50 hover:to-jade-100 dark:hover:from-jade-900/40 dark:hover:to-jade-800/40 hover:text-jade-700 dark:hover:text-jade-300 hover:scale-105 transition-all duration-300 shadow-sm hover:shadow-md cursor-default animate-scaleIn"
                          style={{animationDelay: `${(idx * 100) + (techIdx * 50)}ms`}}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Experience and Education Combined */}
          <div className="portfolio-surface bg-white/85 dark:bg-gray-800/75 backdrop-blur-sm rounded-2xl shadow-lg p-6 transform hover:shadow-2xl transition-all duration-500 border border-gray-100 dark:border-gray-700 hover:border-jade-200 dark:hover:border-jade-500 animate-fadeInUp animation-delay-200">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Experience */}
              <div className="animate-slideInLeft animation-delay-300">
                <div className="flex items-center gap-2 mb-4 group">
                  <div className="p-2 bg-jade-50 rounded-lg group-hover:bg-jade-100 transition-colors duration-300">
                    <svg className="w-5 h-5 text-jade-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Experience</h2>
                </div>

                <div className="space-y-4">
                  {experience.map((exp, index) => (
                    <div key={index} className="group relative pl-16 pb-4 border-l-2 border-gray-200 last:border-l-0 last:pb-0 hover:border-jade-300 transition-colors duration-300 animate-fadeInLeft" style={{animationDelay: `${index * 150}ms`}}>
                      <div className="absolute left-0 top-0 -translate-x-[5px] w-2 h-2 rounded-full bg-jade-400 border-2 border-white group-hover:scale-150 group-hover:bg-jade-500 transition-all duration-300 shadow-sm"></div>
                      
                      {/* Company Logo on the left */}
                      <div className="absolute left-3 top-0 flex items-center">
                        {exp.company.includes('Accenture') && (
                          <img src="/acn.png" alt="Accenture" className="h-8 w-auto object-contain" />
                        )}
                        {exp.company.includes('Quizzle') && (
                          <img src="/quizzlelogo.png" alt="Quizzle" className="h-8 w-auto object-contain" />
                        )}
                        {exp.company.includes('Drafter') && (
                          <>
                            <img src="/drafter.png" alt="Drafter" className="h-8 w-auto object-contain dark:hidden" />
                          </>
                        )}
                        {exp.company.includes('Manning') && (
                          <img src="/mannings.png" alt="The Manning's Realty Group" className="h-8 w-auto object-contain" />
                        )}
                      </div>
                      
                      <div className="mb-1">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 group-hover:text-jade-600 dark:group-hover:text-jade-400 transition-colors duration-300">{exp.role}</h3>
                        <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">{exp.company} • {exp.period}</p>
                      </div>
                      <p className="text-xs text-gray-700 dark:text-gray-400 leading-relaxed">{exp.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Education */}
              <div className="animate-slideInRight animation-delay-400">
                <div className="flex items-center gap-2 mb-4 group">
                  <div className="p-2 bg-jade-50 rounded-lg group-hover:bg-jade-100 transition-colors duration-300">
                    <svg className="w-5 h-5 text-jade-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Education</h2>
                </div>
                
                <div className="group relative pl-6 border-l-2 border-gray-200 hover:border-jade-300 transition-colors duration-300 animate-fadeInRight animation-delay-500">
                  <div className="absolute left-0 top-0 -translate-x-[5px] w-2 h-2 rounded-full bg-jade-400 border-2 border-white group-hover:scale-150 group-hover:bg-jade-500 transition-all duration-300 shadow-sm"></div>
                  
                  <div className="mb-1">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 group-hover:text-jade-600 dark:group-hover:text-jade-400 transition-colors duration-300">BS Information Technology</h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">University of Santo Tomas</p>
                    <p className="text-xs text-gray-500">2022 - 2026 • Manila, Philippines</p>
                  </div>
                  
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <span className="portfolio-chip px-2.5 py-1 bg-jade-50 dark:bg-jade-900/40 text-jade-700 dark:text-jade-300 rounded-full text-xs font-medium hover:scale-105 transition-transform duration-300 shadow-sm cursor-default animate-scaleIn animation-delay-600">Web Development</span>
                    <span className="portfolio-chip px-2.5 py-1 bg-jade-50 dark:bg-jade-900/40 text-jade-700 dark:text-jade-300 rounded-full text-xs font-medium hover:scale-105 transition-transform duration-300 shadow-sm cursor-default animate-scaleIn animation-delay-700">Software Engineering</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          PROJECTS SECTION
          ===================================================== */}
      
          <section id="projects" className="portfolio-section bg-transparent py-8 px-4 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute bottom-20 left-20 w-72 h-72 bg-jade-200 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-4000"></div>
          <div className="absolute top-20 right-20 w-72 h-72 bg-jade-300 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob"></div>
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="portfolio-surface portfolio-surface-strong bg-white/88 dark:bg-gray-800/78 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-100 dark:border-gray-700 animate-fadeInUp animation-delay-600">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 group">
                <div className="p-2 bg-jade-50 dark:bg-jade-900/30 rounded-lg group-hover:bg-jade-100 dark:group-hover:bg-jade-800/40 transition-colors duration-300">
                  <svg className="w-5 h-5 text-jade-600 dark:text-jade-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Recent Projects</h2>
              </div>
              <button 
                onClick={() => setProjectsModalOpen(true)}
                className="portfolio-link-button group flex items-center gap-1 text-jade-500 hover:text-jade-600 dark:text-jade-400 dark:hover:text-jade-300 font-medium text-sm transition-all duration-300"
              >
                View All 
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                </svg>
              </button>
            </div>

            {/* Projects Carousel */}
            <div className="relative">
              {/* Navigation Buttons */}
              <div className="flex justify-center gap-2 mb-6">
                <button
                  onClick={() => setCurrentProjectIndex((prev) => (prev === 0 ? projects.length - 1 : prev - 1))}
                  className="p-2 bg-jade-100 dark:bg-jade-900/30 text-jade-600 dark:text-jade-400 rounded-full hover:bg-jade-200 dark:hover:bg-jade-800/40 transition-all duration-300 disabled:opacity-50"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
                  </svg>
                </button>
                <span className="flex items-center px-3 text-sm text-gray-600 dark:text-gray-400">
                  {currentProjectIndex + 1} / {projects.length}
                </span>
                <button
                  onClick={() => setCurrentProjectIndex((prev) => (prev === projects.length - 1 ? 0 : prev + 1))}
                  className="p-2 bg-jade-100 dark:bg-jade-900/30 text-jade-600 dark:text-jade-400 rounded-full hover:bg-jade-200 dark:hover:bg-jade-800/40 transition-all duration-300"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
              </div>

              {/* Current Project Display */}
              <motion.div
                key={currentProjectIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.4 }}
                className="portfolio-surface bg-white/88 dark:bg-gray-800/82 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
              >
                <div className={`flex flex-col ${currentProjectIndex % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-6 p-6`}>
                  {/* Project Image */}
                  <div className="md:w-1/2">
                    <div
                      className={`aspect-video bg-white dark:bg-gray-700 rounded-lg overflow-hidden border border-jade-100/60 dark:border-gray-600 ${(projects[currentProjectIndex].carouselImages && projects[currentProjectIndex].carouselImages.length > 0) || projects[currentProjectIndex].link !== '#' ? 'cursor-pointer' : ''}`}
                      onClick={() => {
                        const activeProject = projects[currentProjectIndex];

                        if (activeProject.carouselImages && activeProject.carouselImages.length > 0) {
                          setSelectedProject(activeProject);
                          setCurrentCarouselImageIndex(0);
                          setSlideshowOpen(true);
                          return;
                        }

                        const projectLink = activeProject.link;
                        if (projectLink && projectLink !== '#') {
                          window.open(projectLink, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      title={(projects[currentProjectIndex].carouselImages && projects[currentProjectIndex].carouselImages.length > 0) ? 'Open project slideshow' : (projects[currentProjectIndex].link !== '#' ? 'Open project website' : undefined)}
                    >
                      <img 
                        src={projects[currentProjectIndex].image} 
                        alt={projects[currentProjectIndex].title}
                        className={`w-full h-full ${projects[currentProjectIndex].imageFit === 'contain' ? 'object-contain p-4' : 'object-cover'}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `
                            <div class="w-full h-full flex items-center justify-center text-jade-600 dark:text-jade-400">
                              <svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                              </svg>
                            </div>
                          `;
                        }}
                      />
                    </div>
                  </div>

                  {/* Project Info */}
                  <div className="md:w-1/2 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-3">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        {projects[currentProjectIndex].title}
                      </h3>
                      <span className="portfolio-chip px-3 py-1 bg-jade-50 dark:bg-jade-900/40 text-jade-700 dark:text-jade-300 rounded-full text-xs font-semibold shadow-sm">
                        {projects[currentProjectIndex].rating}
                      </span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 leading-relaxed">
                      {projects[currentProjectIndex].description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {projects[currentProjectIndex].technologies.map((tech) => (
                        <span
                          key={tech}
                          className="portfolio-chip px-3 py-1 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-medium shadow-sm"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400 text-sm">{projects[currentProjectIndex].date}</span>
                      {projects[currentProjectIndex].link !== '#' && (
                        <a 
                          href={projects[currentProjectIndex].link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-jade-600 dark:text-jade-400 hover:text-jade-700 dark:hover:text-jade-300 font-medium text-sm transition-all duration-300"
                        >
                          View Project
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          CERTIFICATIONS SECTION
          ===================================================== */}
      
      <section id="certifications" className="portfolio-section bg-transparent py-12 px-4 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-64 h-64 bg-jade-200 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob"></div>
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-jade-300 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-2000"></div>
        </div>

        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-2 group">
              <div className="p-2 bg-jade-50 dark:bg-jade-900/30 rounded-lg group-hover:bg-jade-100 dark:group-hover:bg-jade-800/40 transition-colors duration-300">
                <svg className="w-5 h-5 text-jade-600 dark:text-jade-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Certifications & Achievements</h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Professional certifications and validated skills</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {certifications.map((cert, index) => (
              <motion.a
                key={index}
                href={cert.link}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="portfolio-surface group bg-white/88 dark:bg-gray-800/82 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:border-jade-300 dark:hover:border-jade-500 hover:shadow-lg transition-all duration-300 cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-white dark:bg-gray-700 rounded-lg overflow-hidden flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border border-gray-200 dark:border-gray-600">
                    <img 
                      src={cert.image} 
                      alt={cert.issuer}
                      className="w-full h-full object-contain p-1"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `
                          <svg class="w-6 h-6 text-jade-600 dark:text-jade-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"></path>
                          </svg>
                        `;
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 group-hover:text-jade-600 dark:group-hover:text-jade-400 transition-colors duration-300 mb-1">
                      {cert.title}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{cert.issuer}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{cert.date}</p>
                    {cert.credentialId && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 font-mono mb-1">ID: {cert.credentialId}</p>
                    )}
                    <div className="flex items-center gap-1 text-jade-600 dark:text-jade-400 text-xs">
                      <span>View Certificate</span>
                      <svg className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          FOOTER SECTION
          ===================================================== */}
      
      <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 py-6 px-4 animate-fadeIn animation-delay-1600">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 dark:text-gray-400 text-sm animate-slideInLeft animation-delay-1700">
              © {currentYear} Jade de Lara. All rights reserved.
            </p>
            <div className="flex gap-3 animate-slideInRight animation-delay-1700">
              <a href="https://github.com/jadestack/" target="_blank" rel="noopener noreferrer" className="group w-10 h-10 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg flex items-center justify-center transition-all duration-300 shadow-sm hover:shadow-md" title="GitHub">
                <img src="/github.png" alt="GitHub" className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
              </a>
              <a href="https://linkedin.com/in/jadelloyddelara/" target="_blank" rel="noopener noreferrer" className="group w-10 h-10 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/40 dark:to-blue-800/40 rounded-lg flex items-center justify-center transition-all duration-300 shadow-sm hover:shadow-md" title="LinkedIn">
                <img src="/linkedin.png" alt="LinkedIn" className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
              </a>
            </div>
          </div>
        </div>
      </footer>
      
      </div> {/* End of main content wrapper */}
      
      {/* =====================================================
          INTERACTIVE GAMES & FEATURES
          ===================================================== */}
      
      {/* Snake Game Component */}
      <SnakeGame isOpen={snakeGameOpen} onClose={() => setSnakeGameOpen(false)} />
      
      {/* Typing Test Component */}
      <TypingTest isOpen={typingTestOpen} onClose={() => setTypingTestOpen(false)} />
      
      {/* Dev Console Component */}
      <DevConsole isOpen={devConsoleOpen} onClose={() => setDevConsoleOpen(false)} />
      
      {/* Floating Action Buttons for Games */}
      <div className="fixed bottom-24 right-6 flex flex-col gap-3 z-50">
        <motion.button
          whileHover={{ scale: 1.15 }}
          whileTap={{ scale: 0.85 }}
          onClick={() => setSnakeGameOpen(true)}
          className="w-16 h-16 bg-jade-500 hover:bg-jade-600 text-white rounded-full shadow-lg hover:shadow-2xl transition-all duration-150 flex items-center justify-center group relative border-3 border-white font-bold"
          title="Play Snake Game (Press G)"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="absolute -left-28 bg-gray-900 dark:bg-gray-700 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg">
            Snake Game
          </span>
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.15 }}
          whileTap={{ scale: 0.85 }}
          onClick={() => setTypingTestOpen(true)}
          className="w-16 h-16 bg-purple-500 hover:bg-purple-600 text-white rounded-full shadow-lg hover:shadow-2xl transition-all duration-150 flex items-center justify-center group relative border-3 border-white font-bold"
          title="Typing Test (Press T)"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          <span className="absolute -left-28 bg-gray-900 dark:bg-gray-700 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg">
            Typing Test
          </span>
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.15 }}
          whileTap={{ scale: 0.85 }}
          onClick={() => setDevConsoleOpen(true)}
          className="w-16 h-16 bg-gray-800 hover:bg-gray-900 text-white rounded-full shadow-lg hover:shadow-2xl transition-all duration-150 flex items-center justify-center group relative border-3 border-white font-bold"
          title="Dev Console (Press ~)"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="absolute -left-28 bg-gray-900 dark:bg-gray-700 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg">
            Dev Console
          </span>
        </motion.button>
      </div>
      
      {/* Matrix Mode Overlay Effect */}
      {matrixMode && (
        <div className="fixed inset-0 pointer-events-none z-[60]" style={{ 
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(16, 185, 129, 0.03) 2px, rgba(16, 185, 129, 0.03) 4px)',
          animation: 'matrix-scroll 20s linear infinite'
        }}>
          <div className="absolute inset-0 bg-jade-500/5"></div>
        </div>
      )}
      
    </div>
  );
}

export default App;
